import React from 'react';
// eslint-disable-next-line import/extensions
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider, setLogger as setReactQueryLogger } from 'react-query';
import { Provider } from 'react-redux';
import * as Sentry from '@sentry/browser';
import '@livecommerce/design-system/build/import.css';

import App from './App.jsx';
import Firebase, { FirebaseContext } from './components/Firebase/index.js';
import store from './store/index.js';
import { isProduction, isStage } from './constants/gcp.js';
import Theme from './theme/index.jsx';

import { ClientAPIError } from 'hooks/useBqStatsAPI.jsx';
import { embedBaseUrl } from '../../client-config.js';

const firebase = new Firebase();

setReactQueryLogger({
  error: (error, ...args) => {
    if (error instanceof ClientAPIError) return;
    console.error(error, ...args);
  },
  log: console.log,
  warn: console.warn,
});
const staleTimeFiveMinutes = 5 * 60 * 1000;
const cacheTimeOneHour = 60 * 60 * 1000;
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      staleTime: staleTimeFiveMinutes,
      cacheTime: cacheTimeOneHour,
      queryFn: async ({ queryKey, params = {}, limitToBody = true }) => {
        const agent = await firebase.getAgent();
        const apiPath = `${queryKey[0]}`;
        const cachedSearchParams = queryKey.slice(1).join('&');
        const cachedSearchPath = cachedSearchParams.length > 0 ? `?${cachedSearchParams}` : '';
        const result = await agent.get(`${apiPath}${cachedSearchPath}`, params);
        if (limitToBody) return result.body;
        return result;
      },
    },
  },
});

const IGNORED_ERROR_MESSAGES = [
  'ResizeObserver loop limit exceeded',
  'Module.get_file_info is not a function',
  'FS error',
];

Sentry.init({
  dsn: (process.env.SENTRY_DSN || '').trim(),
  debug: !isProduction,
  environment: isProduction ? 'production' : isStage ? 'stage' : 'unknown',
  release: process.env.RELEASE,
  beforeSend: (event, hint) => {
    const error = hint.originalException;
    const errorMessage = error instanceof Error ? error.message : error;
    if (typeof errorMessage === 'string' && IGNORED_ERROR_MESSAGES.some((message) => errorMessage.includes(message))) {
      return null;
    }
    return event;
  },
});

const root = createRoot(document.getElementById('root'));

if (!window?.BambuserLivecommerce) {
  const embedJsURL = process.env.EMBED_BASE_URL
    ? `${embedBaseUrl}/embed.js`
    : `${embedBaseUrl}/default/embed.js`;

  // Load the embed script
  const script = document.createElement('script');
  script.src = embedJsURL;
  script.async = true;
  document.body.appendChild(script);
}

root.render(
  <Theme>
    <Provider store={store}>
      <FirebaseContext.Provider value={firebase}>
        <QueryClientProvider client={queryClient}>
          <App />
        </QueryClientProvider>
      </FirebaseContext.Provider>
    </Provider>
  </Theme>,
);

if (module.hot) {
  module.hot.accept();
}
